import * as React from 'react';

// Material-UI
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField'

// Styles
import { useTheme } from '@mui/material/styles';

import {
  ClubEmailAddress,
  HTTPHeaders,
  ClubEmailSelectRef // Ensure this is added to your interfaces
} from '../../interfaces';

import {
  API_URL,
  NULL_ID
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

interface ClubEmailSelectProps {
  clubEmailSelected: (clubEmail: ClubEmailAddress) => void;
  clubEmailAddressID: string;
}

/**
 * ClubEmailSelect Component
 * Wrapped in forwardRef to allow the parent to trigger a reset() imperatively.
 */
const ClubEmailSelect = React.forwardRef<ClubEmailSelectRef, ClubEmailSelectProps>((props, ref) => {

  const { getAccessToken } = useAuth0User();

  const [clubEmailAddressList, setClubEmailAddressList] = React.useState<Array<ClubEmailAddress>>([]);

  // #region Styles

  const theme = useTheme();
  const styles = {
    selectWidth: {
      flexGrow: 1,
      margin: theme.spacing(1),
      minWidth: 175,
    },
  };

  // #endregion


  // #region Methods

  /*
  * Fetch club email addresses
  */
  const fetchClubEmails = async () => {
    try {
      const accessToken = await getAccessToken();

      const headers: HTTPHeaders = {};
      headers['Content-Type'] = 'application/json';
      headers['Accept'] = 'application/json';
      headers['Authorization'] = `Bearer ${accessToken}`;

      const response = await fetch(`${API_URL}/v1/email/clubemails`, {
        method: 'GET',
        cache: 'no-cache',
        headers: headers,
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('Club email addresses were not retrieved');
      }

      const ce: Array<ClubEmailAddress> = await response.json();
      setClubEmailAddressList(ce);
    } catch (err) {
      console.error(err);
      // You might want to handle this error with a snackbar or alert
    }
  }

  // #endregion

  // #region Event handlers

  /*
  * Handle selection of club email address
  */
  const handleClubEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedID = Number(event.target.value);

    // If it's a valid ID (not NULL_ID), notify the parent
    const clubEmail = clubEmailAddressList.find(ce => ce.clubEmailAddressID === selectedID);
    if (clubEmail) {
      props.clubEmailSelected(clubEmail);
    }
  };

  React.useEffect(() => {
    fetchClubEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #endregion

  return (
    <TextField
      select
      label='From'
      sx={styles.selectWidth}
      value={props.clubEmailAddressID}
      onChange={handleClubEmailChange}
    >
      <MenuItem key={NULL_ID} value={NULL_ID}>Select From</MenuItem>
      {clubEmailAddressList.map((ce: ClubEmailAddress) => (
        <MenuItem key={ce.clubEmailAddressID} value={ce.clubEmailAddressID}>
          {ce.entityName}
        </MenuItem>
      ))}
    </TextField>
  );
});

// Explicitly set displayName for debugging purposes since we used forwardRef
ClubEmailSelect.displayName = 'ClubEmailSelect';

export default ClubEmailSelect;